import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface ScheduledCreative {
  id: string;
  title: string;
  imageUrl: string;
  orientation: "landscape" | "portrait";
  format: string;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  isPlaying: boolean;
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleDetails: any; // This should ideally be typed more specifically
  fileFormat?: string; // Added for compatibility with ReplaceScheduledCreativeScreen
}

interface ScheduledCreativesContextType {
  scheduledCreatives: ScheduledCreative[];
  addScheduledCreative: (creative: ScheduledCreative) => void;
  updateScheduledCreative: (
    id: string,
    updates: Partial<ScheduledCreative>
  ) => void; // Updated signature
  deleteScheduledCreative: (id: string) => void;
  clearAllScheduledCreatives: () => void;
}

const ScheduledCreativesContext = createContext<
  ScheduledCreativesContextType | undefined
>(undefined);

// Storage key for localStorage
const STORAGE_KEY = "scheduledCreatives";

// Helper functions for localStorage operations
const loadFromStorage = (): ScheduledCreative[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that the data has the expected structure
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (
              item &&
              typeof item === "object" &&
              typeof item.id === "string" &&
              typeof item.title === "string"
            ) {
              // Convert selectedSlots array back to Set if it exists
              if (
                item.scheduleDetails?.selectedSlots &&
                Array.isArray(item.scheduleDetails.selectedSlots)
              ) {
                item.scheduleDetails.selectedSlots = new Set(
                  item.scheduleDetails.selectedSlots
                );
              }
              return item;
            }
            return null;
          })
          .filter((item): item is ScheduledCreative => item !== null);
      }
    }
  } catch (error) {
    console.error(
      "Error loading scheduled creatives from localStorage:",
      error
    );
  }
  return [];
};

const saveToStorage = (creatives: ScheduledCreative[]): void => {
  try {
    // Convert Sets to arrays before saving to localStorage
    const creativesToSave = creatives.map((creative) => {
      if (creative.scheduleDetails?.selectedSlots instanceof Set) {
        return {
          ...creative,
          scheduleDetails: {
            ...creative.scheduleDetails,
            selectedSlots: Array.from(creative.scheduleDetails.selectedSlots),
          },
        };
      }
      return creative;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(creativesToSave));
  } catch (error) {
    console.error("Error saving scheduled creatives to localStorage:", error);
  }
};

export const ScheduledCreativesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [scheduledCreatives, setScheduledCreatives] = useState<
    ScheduledCreative[]
  >([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadedCreatives = loadFromStorage();
    setScheduledCreatives(loadedCreatives);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      saveToStorage(scheduledCreatives);
    }
  }, [scheduledCreatives, isLoaded]);

  const addScheduledCreative = (creative: ScheduledCreative) => {
    setScheduledCreatives((prev) => {
      const updated = [...prev, creative];
      return updated;
    });
  };

  // Updated to accept id and partial updates - compatible with ReplaceScheduledCreativeScreen
  const updateScheduledCreative = (
    id: string,
    updates: Partial<ScheduledCreative>
  ) => {
    setScheduledCreatives((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, ...updates } : c));
      return updated;
    });
  };

  const deleteScheduledCreative = (id: string) => {
    setScheduledCreatives((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      return updated;
    });
  };

  const clearAllScheduledCreatives = () => {
    setScheduledCreatives([]);
  };

  return (
    <ScheduledCreativesContext.Provider
      value={{
        scheduledCreatives,
        addScheduledCreative,
        updateScheduledCreative,
        deleteScheduledCreative,
        clearAllScheduledCreatives,
      }}
    >
      {children}
    </ScheduledCreativesContext.Provider>
  );
};

export const useScheduledCreatives = () => {
  const context = useContext(ScheduledCreativesContext);
  if (context === undefined) {
    throw new Error(
      "useScheduledCreatives must be used within a ScheduledCreativesProvider"
    );
  }
  return context;
};
